import type { ClassDefinition, Role } from "@hd2/data";
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
} from "@headlessui/react";
import { useMemo } from "react";
import { useAssignedClass } from "../hooks/use-assigned-class.ts";
import { useClassPickerState } from "../hooks/use-class-picker.ts";
import { useClasses } from "../hooks/use-classes.ts";

const ROLE_ORDER: Role[] = ["Offense", "Scout", "Support", "Wildcard"];

function ClassRow({
	cls,
	isAssigned,
	onClick,
}: {
	cls: ClassDefinition;
	isAssigned: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`flex w-full cursor-pointer items-center gap-3 border-l-4 p-2.5 text-left transition-colors hover:bg-surface-container-high ${
				isAssigned ? "bg-surface-container-high" : "bg-surface-container-low"
			}`}
			style={{ borderLeftColor: cls.themeColorHex }}
		>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span
						className="truncate font-headline text-sm font-black uppercase tracking-tighter"
						style={{ color: cls.themeColorHex }}
					>
						{cls.name}
					</span>
					{isAssigned && (
						<span className="shrink-0 border border-primary bg-primary/10 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.15em] text-primary">
							{"ASSIGNED"}
						</span>
					)}
				</div>
				<div className="truncate font-mono text-[9px] text-outline-variant uppercase tracking-wide">
					{cls.alias}
				</div>
			</div>
			<span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.15em] text-outline">
				{cls.difficulty}
			</span>
		</button>
	);
}

export function ClassPickerDialog() {
	const [open, setOpen] = useClassPickerState();
	const { classes } = useClasses();
	const [assigned, setAssigned] = useAssignedClass();

	const grouped = useMemo(() => {
		const byRole: Record<Role, ClassDefinition[]> = {
			Offense: [],
			Scout: [],
			Support: [],
			Wildcard: [],
		};
		for (const c of classes) {
			byRole[c.role].push(c);
		}
		for (const role of ROLE_ORDER) {
			byRole[role].sort((a, b) => a.name.localeCompare(b.name));
		}
		return byRole;
	}, [classes]);

	function handleSelect(cls: ClassDefinition) {
		setAssigned({ slug: cls.slug, name: cls.name, role: cls.role });
		setOpen(false);
	}

	function handleClear() {
		setAssigned(null);
		setOpen(false);
	}

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			className="relative z-50"
		>
			<DialogBackdrop className="fixed inset-0 bg-surface/80" />
			<div className="fixed inset-0 flex items-start justify-center p-4 pt-[15vh]">
				<DialogPanel className="flex max-h-[70vh] w-full max-w-lg flex-col border border-outline-variant bg-surface-container-low shadow-[4px_4px_0_0_rgba(0,0,0,1)]">
					<div className="flex items-center justify-between border-b border-outline-variant p-3 md:p-4">
						<DialogTitle className="font-mono text-[10px] text-outline uppercase tracking-[0.2em]">
							{"// SELECT CLASS"}
						</DialogTitle>
						<button
							type="button"
							onClick={() => setOpen(false)}
							className="inline-grid h-7 w-7 cursor-pointer place-items-center"
						>
							<span className="material-symbols-outlined text-outline text-[18px] leading-none">
								close
							</span>
						</button>
					</div>

					{assigned && (
						<div className="flex items-center justify-between border-b border-outline-variant bg-surface-container-lowest px-3 py-2 md:px-4">
							<span className="font-mono text-[10px] text-outline uppercase tracking-wide">
								{`// ASSIGNED: ${assigned.name}`}
							</span>
							<button
								type="button"
								onClick={handleClear}
								className="cursor-pointer border border-outline px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-outline transition-colors hover:border-error hover:text-error"
							>
								{"CLEAR"}
							</button>
						</div>
					)}

					<div className="flex-1 overflow-y-auto">
						{ROLE_ORDER.map((role) => {
							const list = grouped[role];
							if (list.length === 0) {
								return null;
							}
							return (
								<div key={role} className="border-b border-outline-variant">
									<div className="bg-surface-container px-3 py-1.5 font-mono text-[9px] text-outline uppercase tracking-[0.2em] md:px-4">
										{`// ${role.toUpperCase()}`}
									</div>
									<div className="space-y-0.5 p-1.5 md:p-2">
										{list.map((cls) => (
											<ClassRow
												key={cls.slug}
												cls={cls}
												isAssigned={assigned?.slug === cls.slug}
												onClick={() => handleSelect(cls)}
											/>
										))}
									</div>
								</div>
							);
						})}
					</div>
				</DialogPanel>
			</div>
		</Dialog>
	);
}
